import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { filter, map, take } from 'rxjs';

import { CompanyContextService } from '../../services/company-context.service';
import { LicenseContextService } from '../../services/license-context.service';
import { ModuleRegistryService } from '../../services/module-registry.service';
import { MODULE_ROUTE_PREFIX } from './module-routes';

/**
 * Keeps a typed-in URL from opening a module the viewer is not offered — either
 * because the active company holds no licence for it, or because it is not
 * offered to them at all. The sidebar, the palette and the dashboard already
 * withhold such modules; this closes the address bar. A module withheld by
 * LICENSING leads to the "no licence" page, which names the company and the
 * way to UMC (`LicenseContextService.blockedTarget`); one not offered to this
 * viewer at all goes back to the dashboard.
 *
 * Waits for the licensing verdict to settle: on a page load straight into a
 * module UMC has not answered yet, and deciding then would either let an
 * unlicensed module open or bounce a licensed one to the dashboard.
 */
export const licenseGuard: CanActivateFn = (route) => {
  const licenses = inject(LicenseContextService);
  const registry = inject(ModuleRegistryService);
  const router = inject(Router);
  const moduleId = route.routeConfig?.path ?? '';

  // A page load straight into a module can reach this guard before the
  // sign-in effect has started the context load; `idle` would read as
  // "settled" and decide on no data. Idempotent, and the auth guard above has
  // already secured a session.
  inject(CompanyContextService).load();

  const decide = (): boolean | UrlTree => {
    // Audience first: a staff-only module is not this viewer's to open whatever
    // the licensing says, and it is the cheaper question of the two.
    const offered = registry.modules().some((module) => module.id === moduleId);

    if (!offered) {
      return router.createUrlTree(['/', MODULE_ROUTE_PREFIX]);
    }

    if (!licenses.isBlocked(moduleId)) {
      return true;
    }

    return router.createUrlTree(licenses.blockedTarget(moduleId));
  };

  if (licenses.settled()) {
    return decide();
  }

  return toObservable(licenses.settled).pipe(
    filter((settled) => settled),
    take(1),
    map(() => decide()),
  );
};
